/**
 * Motion easing curves (deterministic: the same progress always gives the same value).
 * `enter` / `exit` follow the strong ease-out / ease-in pair used for UI motion; `linear` keeps
 * opacity-only fades identical to the pre-registry deck.
 */
export const MOTION_EASINGS = ["linear", "enter", "exit", "standard"] as const;

export type MotionEasing = (typeof MOTION_EASINGS)[number];

const CURVES: Record<
	Exclude<MotionEasing, "linear">,
	[number, number, number, number]
> = {
	enter: [0.22, 1, 0.36, 1],
	exit: [0.64, 0, 0.78, 0],
	standard: [0.65, 0, 0.35, 1],
};

/** Cubic-bezier y for x in [0, 1] (Newton iterations with a bisection fallback). */
function cubicBezier(
	x: number,
	[x1, y1, x2, y2]: [number, number, number, number],
): number {
	const sample = (t: number, a: number, b: number) =>
		3 * a * t * (1 - t) ** 2 + 3 * b * t ** 2 * (1 - t) + t ** 3;
	const slope = (t: number, a: number, b: number) =>
		3 * a * (1 - t) ** 2 + 6 * (b - a) * t * (1 - t) + 3 * (1 - b) * t ** 2;
	let t = x;
	for (let i = 0; i < 8; i++) {
		const error = sample(t, x1, x2) - x;
		const d = slope(t, x1, x2);
		if (Math.abs(error) < 1e-6) {
			return sample(t, y1, y2);
		}
		if (Math.abs(d) < 1e-6) {
			break;
		}
		t -= error / d;
	}
	let lo = 0;
	let hi = 1;
	t = x;
	for (let i = 0; i < 32; i++) {
		const value = sample(t, x1, x2);
		if (Math.abs(value - x) < 1e-6) {
			break;
		}
		if (value < x) {
			lo = t;
		} else {
			hi = t;
		}
		t = (lo + hi) / 2;
	}
	return sample(t, y1, y2);
}

export function ease(progress: number, easing: MotionEasing): number {
	const x = Math.min(1, Math.max(0, progress));
	if (easing === "linear" || x === 0 || x === 1) {
		return x;
	}
	return cubicBezier(x, CURVES[easing]);
}

export function lerp(from: number, to: number, amount: number): number {
	return from + (to - from) * amount;
}
